import { useId } from "react";

import { AddressAutocomplete } from "./AddressAutocomplete";
import type { Coord } from "@/lib/geo";

export interface AddressParts {
  calle: string;
  zona: string;
  referencias: string;
}

export const emptyAddress: AddressParts = { calle: "", zona: "", referencias: "" };

/** Texto completo tal cual lo escribió la persona (no se sobrescribe nunca). */
export function formatAddress(a: AddressParts): string {
  return [a.calle.trim(), a.zona.trim()].filter(Boolean).join(", ");
}

interface Props {
  titulo: string;
  value: AddressParts;
  onChange: (value: AddressParts) => void;
  /** Coordenadas aproximadas de la colonia/zona, para calcular la ruta. */
  onZoneCoord: (coord: Coord | null) => void;
  placeholderCalle: string;
  placeholderZona: string;
}

export function AddressFields({
  titulo,
  value,
  onChange,
  onZoneCoord,
  placeholderCalle,
  placeholderZona,
}: Props) {
  const id = useId();
  const set = (patch: Partial<AddressParts>) => onChange({ ...value, ...patch });

  return (
    <section aria-labelledby={`${id}-title`}>
      <h4 id={`${id}-title`} className="mb-4 font-display text-sm font-extrabold text-foreground">
        {titulo}
      </h4>

      <div className="space-y-4">
        <div>
          <label htmlFor={`${id}-calle`} className="field-label">
            Calle y número exterior / interior <span className="text-destructive">*</span>
          </label>
          <input
            id={`${id}-calle`}
            type="text"
            className="field-input"
            placeholder={placeholderCalle}
            value={value.calle}
            onChange={(e) => set({ calle: e.target.value })}
            maxLength={160}
            autoComplete="off"
          />
        </div>

        <AddressAutocomplete
          label="Colonia y municipio (CP)"
          placeholder={placeholderZona}
          value={value.zona}
          onChange={(v) => {
            set({ zona: v });
            onZoneCoord(null);
          }}
          onSelect={(labelText, coord) => {
            set({ zona: labelText });
            onZoneCoord(coord);
          }}
        />

        <div>
          <label htmlFor={`${id}-ref`} className="field-label">
            Referencias
          </label>
          <input
            id={`${id}-ref`}
            type="text"
            className="field-input"
            placeholder="Referencias o indicaciones para el chofer (opcional)"
            value={value.referencias}
            onChange={(e) => set({ referencias: e.target.value })}
            maxLength={200}
            autoComplete="off"
          />
        </div>
      </div>
    </section>
  );
}
